import type { NetworkResource } from './types';

/**
 * Deep link URL templates for GCP and AWS console resources.
 * Placeholders use {fieldName} syntax and are resolved from resource attributes.
 */
const DEEP_LINK_TEMPLATES: Record<string, Record<string, string>> = {
  gcp: {
    vpc: 'https://console.cloud.google.com/networking/networks/details/{name}?project={project}',
    subnet: 'https://console.cloud.google.com/networking/subnetworks/details/{region}/{name}?project={project}',
    firewall_rule: 'https://console.cloud.google.com/networking/firewalls/details/{name}?project={project}',
    firewall_policy: 'https://console.cloud.google.com/networking/firewalls/policies/{name}?project={project}',
    vpn_tunnel: 'https://console.cloud.google.com/hybrid/vpn/tunnels/details/{region}/{name}?project={project}',
    vpn_gateway: 'https://console.cloud.google.com/hybrid/vpn/gateways/details/{region}/{name}?project={project}&isHA={isHA}',
    cloud_router: 'https://console.cloud.google.com/hybrid/routers/details/{region}/{name}?project={project}',
    route_table: 'https://console.cloud.google.com/networking/routes/details/{name}?project={project}',
    load_balancer: 'https://console.cloud.google.com/net-services/loadbalancing/list/loadBalancers?project={project}',
    backend_service: 'https://console.cloud.google.com/net-services/loadbalancing/list/loadBalancers?project={project}',
    target_proxy: 'https://console.cloud.google.com/net-services/loadbalancing/list/loadBalancers?project={project}',
    url_map: 'https://console.cloud.google.com/net-services/loadbalancing/details/http/{name}?project={project}',
    target_group: 'https://console.cloud.google.com/net-services/loadbalancing/advanced/targetPools/details/regions/{region}/targetPools/{name}?project={project}',
    dns_zone: 'https://console.cloud.google.com/net-services/dns/zones/{name}?project={project}',
    dns_record: 'https://console.cloud.google.com/net-services/dns/zones/{zone}/rrsets/{name}/{type}/view?project={project}',
    nat_gateway: 'https://console.cloud.google.com/net-services/nat/details/{region}/{router}/{name}?project={project}&tab=details',
    psc_endpoint: 'https://console.cloud.google.com/networking/psc/details/{region}/{name}?project={project}',
    psc_service_attachment: 'https://console.cloud.google.com/net-services/psc/producers/details/{region}/{name}/overview?project={project}',
    peering: 'https://console.cloud.google.com/networking/peering/details/{networkName}/{name}?project={project}',
    partner_interconnect: 'https://console.cloud.google.com/hybrid/attachments/details/{region}/{name}?project={project}&tab=details',
    dedicated_interconnect: 'https://console.cloud.google.com/hybrid/interconnects/{name}?project={project}',
    ncc_hub: 'https://console.cloud.google.com/hybrid/hubs/details/{name}?project={project}',
    ncc_spoke: 'https://console.cloud.google.com/hybrid/hubs/spokes/details/{region}/{name}?project={project}',
    cloud_armor_policy: 'https://console.cloud.google.com/net-security/securitypolicies/details/{name}?project={project}',
    packet_mirroring: 'https://console.cloud.google.com/networking/packetmirroring/{region}/{name}?project={project}',
    dns_policy: 'https://console.cloud.google.com/net-services/dns/policies/{name}/view?project={project}',
    service_networking_connection: 'https://console.cloud.google.com/networking/peering/details/{networkName}/{name}?project={project}',
    shared_vpc: 'https://console.cloud.google.com/networking/xpn/details?project={project}',
  },
  aws: {
    vpc: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#VpcDetails:VpcId={id}',
    subnet: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#SubnetDetails:subnetId={id}',
    security_group: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#SecurityGroup:groupId={id}',
    network_acl: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#NetworkAclDetails:networkAclId={id}',
    route_table: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#RouteTableDetails:RouteTableId={id}',
    internet_gateway: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#InternetGateway:internetGatewayId={id}',
    nat_gateway: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#NatGatewayDetails:natGatewayId={id}',
    vpn_connection: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#VpnConnectionDetails:VpnConnectionId={id}',
    vpn_gateway: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#VpnGatewayDetails:VpnGatewayId={id}',
    customer_gateway: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#CustomerGatewayDetails:CustomerGatewayId={id}',
    transit_gateway: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#TransitGatewayDetails:transitGatewayId={id}',
    transit_gateway_attachment: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#TransitGatewayAttachmentDetails:transitGatewayAttachmentId={id}',
    transit_gateway_route_table: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#TransitGatewayRouteTables:transitGatewayRouteTableId={id}',
    direct_connect_connection: 'https://{region}.console.aws.amazon.com/directconnect/v2/home?region={region}#/connections/{id}',
    direct_connect_virtual_interface: 'https://{region}.console.aws.amazon.com/directconnect/v2/home?region={region}#/virtual-interfaces/{id}',
    direct_connect_gateway: 'https://console.aws.amazon.com/directconnect/v2/home#/dxgateways/{id}',
    load_balancer: 'https://{region}.console.aws.amazon.com/ec2/home?region={region}#LoadBalancer:loadBalancerArn={id}',
    target_group: 'https://{region}.console.aws.amazon.com/ec2/home?region={region}#TargetGroup:targetGroupArn={id}',
    dns_zone: 'https://console.aws.amazon.com/route53/v2/hostedzones#ListRecordSets/{id}',
    dns_record: 'https://console.aws.amazon.com/route53/v2/hostedzones#ListRecordSets/{zone}',
    vpc_endpoint: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#EndpointDetails:vpcEndpointId={id}',
    vpc_endpoint_service: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#EndpointServiceDetails:endpointServiceId={id}',
    peering: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#PeeringConnectionDetails:VpcPeeringConnectionId={id}',
    network_firewall: 'https://{region}.console.aws.amazon.com/vpc/home?region={region}#NetworkFirewallDetails:firewallArn={id}',
    waf_web_acl: 'https://console.aws.amazon.com/wafv2/homev2/web-acl/{name}/{id}/overview',
    waf_rule_group: 'https://console.aws.amazon.com/wafv2/homev2/rule-group/{name}/{id}',
    elastic_ip: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#ElasticIpDetails:AllocationId={id}',
    network_interface: 'https://{region}.console.aws.amazon.com/ec2/home?region={region}#NetworkInterface:networkInterfaceId={id}',
    prefix_list: 'https://{region}.console.aws.amazon.com/vpcconsole/home?region={region}#ManagedPrefixListDetails:PrefixListId={id}',
    cloud_wan_core_network: 'https://console.aws.amazon.com/networkmanager/home#/networks/{id}/core-network',
    global_network: 'https://console.aws.amazon.com/networkmanager/home#/networks/{id}',
    network_manager_site: 'https://console.aws.amazon.com/networkmanager/home#/networks/{networkId}/sites/{id}',
    network_manager_link: 'https://console.aws.amazon.com/networkmanager/home#/networks/{networkId}/links/{id}',
    network_manager_device: 'https://console.aws.amazon.com/networkmanager/home#/networks/{networkId}/devices/{id}',
    global_accelerator: 'https://console.aws.amazon.com/globalaccelerator/home#/accelerator/{id}',
    global_accelerator_listener: 'https://console.aws.amazon.com/globalaccelerator/home#/accelerator/{acceleratorId}/listener/{id}',
    global_accelerator_endpoint_group: 'https://console.aws.amazon.com/globalaccelerator/home#/accelerator/{acceleratorId}/listener/{listenerId}/endpoint-group/{id}',
    cloudfront_distribution: 'https://console.aws.amazon.com/cloudfront/v4/home#/distributions/{id}',
    resolver_endpoint: 'https://{region}.console.aws.amazon.com/route53resolver/home?region={region}#/endpoint/{id}',
    resolver_rule: 'https://{region}.console.aws.amazon.com/route53resolver/home?region={region}#/rule/{id}',
  },
};

/**
 * Resolves all placeholders in a URL template using resource attributes.
 * Returns null if any required placeholder cannot be resolved.
 */
function resolvePlaceholders(template: string, values: Record<string, string | null | undefined>): string | null {
  const placeholderRegex = /\{(\w+)\}/g;
  let result = template;
  let match: RegExpExecArray | null;

  // Find all placeholders and check they can be resolved
  while ((match = placeholderRegex.exec(template)) !== null) {
    const key = match[1];
    const value = values[key];
    if (value == null || value === '') {
      return null;
    }
    result = result.replaceAll(`{${key}}`, value);
  }

  return result;
}

/**
 * Generates a deep link URL to the native cloud console for a network resource.
 * Returns null when the resource type is unsupported or required attributes are missing.
 */
export function generateDeepLink(resource: NetworkResource): string | null {
  const providerTemplates = DEEP_LINK_TEMPLATES[resource.provider];
  if (!providerTemplates) {
    return null;
  }

  const template = providerTemplates[resource.resourceType];
  if (!template) {
    return null;
  }

  // Build the values map from resource attributes
  const values: Record<string, string | null | undefined> = {
    project: resource.projectOrAccount,
    region: resource.region,
    name: resource.name,
    id: resource.nativeId,
  };

  // GCP-specific builder functions for LB-related resources
  if (resource.provider === 'gcp') {
    // For load_balancer (forwarding rule), build appropriate deep link
    if (resource.resourceType === 'load_balancer' && resource.name) {
      const url = buildLoadBalancerLink(resource);
      if (url) return url;
    }

    // For target_proxy, derive the url_map name and link to the LB detail
    if (resource.resourceType === 'target_proxy' && resource.name) {
      const url = buildTargetProxyLink(resource);
      if (url) return url;
    }

    // For backend_service, build direct link to backend details
    if (resource.resourceType === 'backend_service' && resource.name) {
      const url = buildBackendServiceLink(resource);
      if (url) return url;
    }

    // For url_map, distinguish between regional and global
    if (resource.resourceType === 'url_map' && resource.name) {
      const url = buildUrlMapLink(resource);
      if (url) return url;
    }
  }

  // GCP VPN Gateway: determine HA vs Classic from nativeId (selfLink)
  if (resource.provider === 'gcp' && resource.resourceType === 'vpn_gateway') {
    const isHA = !resource.nativeId?.includes('/targetVpnGateways/');
    values.isHA = isHA ? 'true' : 'false';
  }

  // Add metadata fields for templates that need them
  const metadata = resource.metadata;
  if (metadata) {
    // GCP-specific metadata fields
    if (metadata.router != null) values.router = String(metadata.router);
    if (metadata.routerName != null && values.router == null) values.router = String(metadata.routerName);
    if (metadata.networkName != null) values.networkName = String(metadata.networkName);
    // Extract VPC name from selfLink-style network field (e.g. "projects/.../networks/ripley-vpc")
    if (values.networkName == null && metadata.network != null) {
      const networkStr = String(metadata.network);
      const networkMatch = networkStr.match(/\/networks\/([^/]+)$/);
      if (networkMatch) values.networkName = networkMatch[1];
    }
    if (metadata.zone != null) values.zone = String(metadata.zone);
    if (metadata.managedZone != null) values.zone = String(metadata.managedZone);
    if (metadata.type != null) values.type = String(metadata.type);

    // AWS-specific metadata fields
    if (metadata.networkId != null) values.networkId = String(metadata.networkId);
    if (metadata.acceleratorId != null) values.acceleratorId = String(metadata.acceleratorId);
    if (metadata.listenerId != null) values.listenerId = String(metadata.listenerId);
  }

  return resolvePlaceholders(template, values);
}

/**
 * Derives the URL Map name from a forwarding rule or target proxy name using naming conventions.
 * Common patterns:
 *   *-rfwd-*   → *-rumap-*  (regional internal forwarding rule → regional url map)
 *   *-efwd-*   → *-eumap-*  (regional external forwarding rule → regional url map)
 *   *-fwd-*    → *-gumap-*  (global forwarding rule → global url map)
 *   *-rproxy-* → *-rumap-*  (regional internal target proxy → regional url map)
 *   *-eproxy-* → *-eumap-*  (regional external target proxy → regional url map)
 *   *-proxy-*  → *-gumap-*  (global target proxy → global url map)
 */
function deriveUrlMapName(name: string, regional: boolean): string | null {
  if (regional) {
    // Try internal regional patterns first
    let derived = name.replace(/-rfwd-/, '-rumap-').replace(/-rproxy-/, '-rumap-');
    if (derived !== name) return derived;
    // Try external regional patterns
    derived = name.replace(/-efwd-/, '-eumap-').replace(/-eproxy-/, '-eumap-');
    if (derived !== name) return derived;
    return null;
  }
  const derived = name.replace(/-fwd-/, '-gumap-').replace(/-proxy-/, '-gumap-');
  return derived !== name ? derived : null;
}

/**
 * Builds a GCP Console deep link for a load_balancer (forwarding rule) resource.
 */
function buildLoadBalancerLink(resource: NetworkResource): string | null {
  const name = resource.name;
  const project = resource.projectOrAccount;
  const region = resource.region;

  if (!project) return null;

  const base = 'https://console.cloud.google.com/net-services/loadbalancing/details';

  // K8s pattern: k8s2-XX-{hash}-{name} → k8s2-um-{hash}-{name}
  const k8sMatch = name.match(/^k8s2-[a-z]{2}-(.+)$/);
  if (k8sMatch) {
    const urlMap = `k8s2-um-${k8sMatch[1]}`;

    // Regional k8s LB (has region)
    if (region) {
      return `${base}/internalRegionalHttp/${encodeURIComponent(region)}/${encodeURIComponent(urlMap)}?project=${encodeURIComponent(project)}`;
    }

    // Global k8s LB
    return `${base}/http/${encodeURIComponent(urlMap)}?project=${encodeURIComponent(project)}`;
  }

  // Regional LB (has region, no k8s2 prefix)
  if (region && !name.startsWith('k8s2-')) {
    const scheme = resource.metadata?.loadBalancingScheme as string | undefined;
    if (scheme === 'INTERNAL') {
      return `${base}/internal/${encodeURIComponent(region)}/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`;
    }
    if (scheme === 'INTERNAL_MANAGED') {
      // Derive the url_map name for the detail page
      const urlMapName = deriveUrlMapName(name, true);
      if (urlMapName) {
        return `${base}/internalRegionalHttp/${encodeURIComponent(region)}/${encodeURIComponent(urlMapName)}?project=${encodeURIComponent(project)}`;
      }
      return `${base}/internalRegionalHttp/${encodeURIComponent(region)}/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`;
    }
    if (scheme === 'EXTERNAL_MANAGED') {
      const urlMapName = deriveUrlMapName(name, true);
      if (urlMapName) {
        return `${base}/http/${encodeURIComponent(urlMapName)}?project=${encodeURIComponent(project)}&region=${encodeURIComponent(region)}`;
      }
      return `${base}/http/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}&region=${encodeURIComponent(region)}`;
    }
    // Check metadata target to determine if it's an HTTP(S) LB
    const target = resource.metadata?.target as string | undefined;
    if (target && (target.includes('/targetHttpProxies/') || target.includes('/targetHttpsProxies/'))) {
      // Regional HTTP(S) LB — link via url_map name
      const urlMapName = deriveUrlMapName(name, true);
      if (urlMapName) {
        return `${base}/internalRegionalHttp/${encodeURIComponent(region)}/${encodeURIComponent(urlMapName)}?project=${encodeURIComponent(project)}`;
      }
    }
    // Try to derive url_map from naming convention even without scheme/target metadata
    const urlMapName = deriveUrlMapName(name, true);
    if (urlMapName) {
      return `${base}/internalRegionalHttp/${encodeURIComponent(region)}/${encodeURIComponent(urlMapName)}?project=${encodeURIComponent(project)}`;
    }
    // For forwarding rules without a derivable url_map (e.g., VPN rules, plain network LBs),
    // check if it looks like a network LB (TCP/UDP with target pool or backend service)
    const targetMeta = resource.metadata?.target as string | undefined;
    const isNetworkLB = targetMeta && (targetMeta.includes('/targetPools/') || targetMeta.includes('/backendServices/'));
    if (isNetworkLB) {
      return `${base}/network/${encodeURIComponent(region)}/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`;
    }
    // VPN forwarding rules → link to the VPN gateway detail page
    if (targetMeta && targetMeta.includes('/targetVpnGateways/')) {
      const gwName = targetMeta.split('/').pop() || '';
      if (gwName) {
        return `https://console.cloud.google.com/hybrid/vpn/gateways/details/${encodeURIComponent(region)}/${encodeURIComponent(gwName)}?project=${encodeURIComponent(project)}&isHA=false`;
      }
    }
    // PSC forwarding rules → link to PSC endpoint detail
    if (targetMeta && targetMeta.includes('/serviceAttachments/')) {
      return `https://console.cloud.google.com/net-services/psc/details/${encodeURIComponent(region)}/${encodeURIComponent(name)}/overview?project=${encodeURIComponent(project)}`;
    }
    // Other non-LB forwarding rules → link to LB list (best available)
    return `https://console.cloud.google.com/net-services/loadbalancing/list/loadBalancers?project=${encodeURIComponent(project)}`;
  }

  // Global non-k8s LB
  if (!region && !name.startsWith('k8s2-')) {
    const urlMapName = deriveUrlMapName(name, false);
    if (urlMapName) {
      return `${base}/httpAdvanced/${encodeURIComponent(urlMapName)}?project=${encodeURIComponent(project)}`;
    }
    // If target is an HTTP(S) proxy, try to derive url_map from the proxy name
    const globalTarget = resource.metadata?.target as string | undefined;
    if (globalTarget && (globalTarget.includes('/targetHttpProxies/') || globalTarget.includes('/targetHttpsProxies/'))) {
      const proxyName = globalTarget.split('/').pop() || '';
      const urlMapFromProxy = deriveUrlMapName(proxyName, false);
      if (urlMapFromProxy) {
        return `${base}/httpAdvanced/${encodeURIComponent(urlMapFromProxy)}?project=${encodeURIComponent(project)}`;
      }
      // Can't derive url_map → link to LB list with frontends tab
      return `https://console.cloud.google.com/net-services/loadbalancing/list/loadBalancers?project=${encodeURIComponent(project)}`;
    }
    // Non-HTTP global LB (e.g., TCP/SSL proxy) → LB list
    return `https://console.cloud.google.com/net-services/loadbalancing/list/loadBalancers?project=${encodeURIComponent(project)}`;
  }

  // Fallback: list of LBs
  return `https://console.cloud.google.com/net-services/loadbalancing/list/loadBalancers?project=${encodeURIComponent(project)}`;
}

/**
 * Builds a GCP Console deep link for a target_proxy resource.
 * Points directly to the target proxy detail page.
 */
function buildTargetProxyLink(resource: NetworkResource): string | null {
  const name = resource.name;
  const project = resource.projectOrAccount;
  const region = resource.region;

  if (!project) return null;

  const base = 'https://console.cloud.google.com/net-services/loadbalancing/components';

  // Determine proxy type from metadata.proxyType
  const proxyType = resource.metadata?.proxyType as string | undefined;
  let componentPath = 'target-https-proxies'; // default
  if (proxyType === 'targetHttpProxies') {
    componentPath = 'target-http-proxies';
  } else if (proxyType === 'targetHttpsProxies') {
    componentPath = 'target-https-proxies';
  } else if (proxyType === 'targetTcpProxies') {
    componentPath = 'target-tcp-proxies';
  } else if (proxyType === 'targetSslProxies') {
    componentPath = 'target-ssl-proxies';
  }

  // Scope is region or "global"
  const scope = region ? encodeURIComponent(region) : 'global';

  return `${base}/${componentPath}/details/${scope}/${encodeURIComponent(name)}/details?project=${encodeURIComponent(project)}`;
}

/**
 * Builds a GCP Console deep link for a url_map resource.
 * Regional URL Maps use internalRegionalHttp path; global ones use http.
 */
function buildUrlMapLink(resource: NetworkResource): string | null {
  const name = resource.name;
  const project = resource.projectOrAccount;
  const region = resource.region;

  if (!project) return null;

  const base = 'https://console.cloud.google.com/net-services/loadbalancing/details';

  // K8s pattern: k8s2-um-{hash}-{name}
  const k8sMatch = name.match(/^k8s2-um-(.+)$/);
  if (k8sMatch) {
    if (region) {
      return `${base}/internalRegionalHttp/${encodeURIComponent(region)}/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`;
    }
    return `${base}/http/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`;
  }

  // Regional URL Map
  if (region) {
    return `${base}/internalRegionalHttp/${encodeURIComponent(region)}/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`;
  }

  // Global URL Map
  return `${base}/http/${encodeURIComponent(name)}?project=${encodeURIComponent(project)}`;
}

/**
 * Builds a GCP Console deep link for a backend_service resource.
 */
function buildBackendServiceLink(resource: NetworkResource): string | null {
  const name = resource.name;
  const project = resource.projectOrAccount;
  const region = resource.region;

  if (!project) return null;

  const base = 'https://console.cloud.google.com/net-services/loadbalancing/backends/details';

  if (region) {
    // Regional backend service
    return `${base}/regionalBackendService/${encodeURIComponent(region)}/${encodeURIComponent(name)}/details?project=${encodeURIComponent(project)}`;
  }

  // Global backend service
  return `${base}/backendService/${encodeURIComponent(name)}/details?project=${encodeURIComponent(project)}`;
}
